// new helpers to work with QuoteSheetBuilder using groups instead of tree data

import Vue from 'vue';
import { v4 as uuidv4 } from 'uuid';

import {
    //FormatCurrencyHelper, 
    hasValueOrZero, 
    toNumberOrNull,
    //random22id,
} from '../../js/filters-helpers';
import { vendorColsAll } from './quote-sheet-vendor-cols';


// new version without NESTED linked_product (just ID instead)
class QuoteSheetItem {
    constructor({ // don't set default values for most things, unless it really makes sense AND they are optional on front-end
        id = '', // DO NOT SET ON FRONTEND, but keep it if it was already set on incoming data from backend
        name = '',  // optional, but only if we have linked_product, otherwise it should be provided
        description = '',  // optional
        guid,  // guid as used on front-end, required because we use this as 'placeholder' for FK from variants
        sort_order = null,  // sort order in overall list, (one-based, NOT zero-based), not used until we output in API format
        part_number = '', // ONLY USE IF WE DON'T HAVE LINKED_PRODUCT
        linked_product,  // optional full object of linked_product, if from API -- we'll just grab ID
        linked_product_id = '',  // OR pass in ID of linked_product directly -- this has priority over linked_product.id
        quotesheet_id = '',  // may set this in backend instead... just set it when reconstructing to send back to API???  or not needed on frontend at all? (sounds best)
        use_pn_for_name = false,  // NEW Aug 9 2022 - use part_number INSTEAD of name?  will make part_number required, and use that with linked_product instead
        // if you use_pn_for_name, there is also some stuff in QuoteSheetBuilder to change... params passed to item renderer, logic on when you change cols visible, etc.
        // Feb 17 2023 - we no longer have name required on items.Product model, can be blank -- but we still need one or the other on QuoteSheetItem 
    }) {
        //console.log('Hello from the top of NEW QuoteSheetItem constructor');
        this.id = id;  // do NOT set a default, we will keep using guid so all PKs are set by backend
        this.use_pn_for_name = use_pn_for_name;
        // REMOVED Feb 23 2023 - can have blank name now - this.name = use_pn_for_name ? part_number : name;
        this.name = name;
        this.description = description || linked_product?.description || '';  // use description from linked_product if there is one and no specific description provided! - but description can be overridden on here, does not propogate to linked_product
        this.guid = guid;  // USE THIS as 'FK' on front-end from QuoteSheetItemVariants
        this.sort_order = toNumberOrNull(sort_order) || 0;  // fallback to zero so we always have a number, helpful when we're calling increment method below
        this.part_number = part_number;
        // DEAL WITH linked_product -- NOTE it might be in constructor params, but might be null or undefined {linked_product: null}
        this.linked_product_id = linked_product_id || (!!linked_product && linked_product.id) ? linked_product.id : '';  // make sure you have a final default that makes sense ('')
        this.quotesheet_id = quotesheet_id;
        //console.log('Hello from the END of NEW QuoteSheetItem constructor');
    }
    // GETTERS
    /* get linked_product_id() {
        return this.linked_product.id || '';
    } */
    get hasLinkedProduct() {
        return !!this.linked_product_id;
    }
    /* get itemName() {  // since we don't have a NESTED linked_product, we can't lookup from that... so just return 'this.name'
        // only needed for backwards compatibility... otherwise just use this.name
        // Aug 9 2022 - not used anymore???
        return (this.use_pn_for_name ? this.part_number : this.name) || '';  // return empty string as default in case we didn't set name somehow
    } */
    get partNumber() {  // since we don't have a NESTED linked_product, we can't lookup from that... so just return 'this.part_number'
        // only needed for backwards compatibility... otherwise just use this.name
        return this.part_number || '';
    }
    // STATIC (class methods/properties)
    static disabledValues() {
        // return an OBJECT with the correct values for each field when that field is disabled (ie: column turned off in grid)
        // only include fields/cols that can be turned off
        return {
            //name: '',  // even if use_pn_for_name, we don't need value here... just make sure formatForApi returns something for it
            description: '',
            part_number: '',
        }
    }
    // METHODS
    /* setUsePnForName(usePnForName) {  // not calling from parent anymore?  Feb 23 2023 - because we can have name blank now
        // for setting/updating this on existing instances... like if 'name' column is turned on/off after creating sheet
        // must refresh the grid/validation after this also...
        // pass in true/false EXPLICITLY for setting this.use_pn_for_name
        if (![true, false].includes(usePnForName)) {
            throw new Error('usePnForName must be true or false explicitly');
        }
        // update the value... then if turning ON, overwrite this.name with this.part_number also
        this.use_pn_for_name = usePnForName;
        if (usePnForName) {
            this.name = this.part_number;
        }  // don't need to go the other way...
    } */
    canCreateLinkedProduct() {
        // return BOOLEAN -- can we make save a new LinkedProduct with this?  must not have existing linked product, and must have some fields
        // Feb 17 2023 - changed to require EITHER part_number or name, not dependent on use_pn_for_name 
        //let requiredFields = this.use_pn_for_name ? ['part_number'] : ['name'];
        //let requiredFieldsOk = requiredFields.filter(fld => !hasValueOrZero(this[fld]));
        const requiredFieldsOk = hasValueOrZero(this.name) || hasValueOrZero(this.part_number);
        return requiredFieldsOk && !this.hasLinkedProduct;
    }
    getCreateLinkedProductData() {
        // return an OBJECT with data formatted to be sent to create method on Item/Product endpoint for saving a new linked product to DB
        return {
            //name: this.use_pn_for_name ? this.part_number : this.name,
            name: this.name ?? '',  // Feb 17 2023 - 'name' is no longer required on items.Product model -- so we will no longer assign part_number to name IN HERE if no name is shown or provided
            part_number: this.part_number ?? '',
            description: this.description ?? '',  // Feb 17: added this back... seems to make sense to use description if there is one, and product/item isn't saved yet?
            // add target_price / current_price and currencies???
        }
    }
    setLinkedProduct(linked_product_id) {
        // use this to set a newly created LinkedProduct on this qsItem -- like a setter, but not, so you can pass in more args if needed
        // should you clear out name, description, part_number from this objects local values??? so you can get them from linked product instead?
        if (!linked_product_id) {return} // bail early if you don't have a truthy value for linked_product_id
        this.linked_product_id = linked_product_id;
        // clear out local fields here that will be 'replaced' by values on linked_product instead
        this.name = '';
        this.part_number = '';
        // this.description = '';  // NO LONGER -- if you blank it, you must also use it when creating linkedProduct
        // add more - prices with currencies?
    }
    clearLinkedProduct() {
        // break the relationship with linked product -- although if you have auto P/N matching happening on backend, it might get linked automatically again on save
        // also copy the 'name' / 'part_number' from the previous linked product, if this doesn't have its own?
        // or maybe no need.... just keep whatever we had... if it was blank, then it will get removed, as it should I think
        this.linked_product_id = '';
    }
    incrementSortOrder() {
        // a convenience method to quickly increment++ the .sort_order when shifting order during drag-n-drop operations
        // putting it in a convenience method in case we need to add extra logic
        this.sort_order++;
    }
    decrementSortOrder() {
        this.sort_order--;
    }
    formatForApi(overrideObj) {  // return version of itself in format ready to POST to API
        // can pass in optional overrideObj -- object that will override any key/values in here before returning it
        // make sure to set the default 'blank' value in here that serializer expects (strings or null)
        let apiObj = {  // this makes a new object, so no need to clone in caller
            id: this.id,  // required
            //name: (this.use_pn_for_name ? this.part_number : this.name) ?? '',
            name: this.name ?? '',  // Feb 17 - no need to set to part_number -- name can now be blank, and its easier to fill later if needed than the other way around 
            description: this.description ?? '',
            guid: this.guid,  // required
            sort_order: this.sort_order,  // required
            part_number: this.part_number ?? '',
            linked_product_id: this.linked_product_id,  // nullable
        }
        if (overrideObj) {
            apiObj = Object.assign(apiObj, overrideObj);
        }
        return apiObj;
    }
    clone(initialArgs) {
        // clone this object, resetting certain things as needed
        // initialArgs -- an optional OBJECT of initial key/values to use - can set anything EXCEPT 'id' and 'guid' -- those get reset no matter what
        let cloneArgs = {  // make sure this matches the object args in constructor!
            // id: '',  // must reset ID to blank when cloning
            name: this.name, 
            description: this.description,
            // guid: uuidv4(),  // must set a new guid when cloning
            sort_order: this.sort_order,  // if you want to reset it, use initialArgs
            part_number: this.part_number,
            linked_product_id: this.linked_product_id,
            quotesheet_id: this.quotesheet_id,
            use_pn_for_name: this.use_pn_for_name,
        };
        if (initialArgs) {
            cloneArgs = Object.assign(cloneArgs, initialArgs);
        }
        // now make sure 'id' and 'guid' are reset, so we don't accidentally use existing ones in initialArgs
        cloneArgs.id = '';  // always blank this since its not saved to backend yet
        cloneArgs.guid = uuidv4();  // always assign new guid
        return new QuoteSheetItem(cloneArgs);
    }
    getInvalidFields() {
        // return an ARRAY of field / property names that HAVE ERRORS / are NOT valid 
        // used so you can highlight those cells specifically
        //let requiredFields = ['guid'];
        let invalidFields = [];
        if (!this.guid) {
            invalidFields.push('guid');
        }
        if (!this.hasLinkedProduct) {
            // FEB 17 2023 - we only need name OR part_number if no linked product now -- we'll highlight both if BOTH are blank
            if (!this.name && !this.part_number) {
                invalidFields.push('name', 'part_number');
            }  // but if only one is blank, then both are valid 
        }
            //     // make sure we have a name/part_number if we don't have linked_product
        //     if (this.use_pn_for_name) {
        //         requiredFields.push('part_number');
        //     } else {
        //         requiredFields.push('name');  
        //     }
        // }
        //return requiredFields.filter(fld => !hasValueOrZero(this[fld]));  // will return empty array if all OK
        return invalidFields;
    }
    isValid() {
        // validate if this QuoteSheetItem is valid on its own
        /* let requiredFields = ['guid'];
        if (!this.hasLinkedProduct) {
            requiredFields.push('name');  // make sure we have a name if we don't have linked_product
        } */
        //console.log(`From isValid on QuoteSheetItem guid ${this.guid} - required fields below:`);
        //console.log(requiredFields);
        // if we have a nested LinkedProduct, also check that its valid
        // OLD WAY let requiredFieldsOk = requiredFields.every(fld => hasValueOrZero(this[fld]));
        return this.getInvalidFields().length === 0;
    }
    isEmpty() {
        // is this QS ITEM completely empty?  if true, AND IF variant(s) also completely empty, we'll discard this before saving via API
        // what is empty?  no text entered in non-vendor-input cells, no linked product
        const emptyCheckFields = [
            'name', 'description', 'part_number', 'linked_product_id',
        ];
        // make sure they're all falsey?  or different than default values for them?  
        // checking if different than defaults seems more strict, but maybe too strict
        // just check if all falsey for now
        return emptyCheckFields.every(fld => !this[fld]);
    }
}

class QuoteSheetItemVariant {
    constructor({
        id = '',  
        guid,  // guid as used on front-end, so we let backend set all real PKs
        quotesheet_item = {},  // might be the nested object, passed in from API data -- if here, we'll just grab the ID or guid - default to nested object so we don't raise error if looking up quotesheet_item.id
        //quotesheet_item_id = '',  // OR, just pass in the quotesheet_item_id -- this has priority
        description = '',  // optional
        sort_order = null,  // sort order relative to PARENT, (one-based, NOT zero-based), only used when we output in API format
        quantity = null,
        uom = '',
        quantity_timeframe = '',
        need_by_date = null,
        target_price = null,
        target_price_currency = '',
        current_price = null,
        current_price_currency = '',
        enable_price = true,
        enable_subtotal = true, 
        //enable_quoted_price = true,  // we are just using enable_price / enable_subtotal
        //enable_quoted_subtotal = true,  // we are just using enable_price / enable_subtotal
        enable_comment = true,
        enable_moq = true,
        enable_leadtime = true,
        enable_deliverydate = true,
        enable_country_of_origin = true,
        enable_shipterms = true,
        enable_payterms = true,
        enable_nobid = true,
        qs_item_guid = '',  // the 'FK' to the QuoteSheetItem -- will always be available on front-end, QuoteSheetItem ID will not be
        default_currency = '',  // optional, if passed in we can use it to set target_price_currency and current_price_currency if those are blank -- ONLY USED IN CONSTRUCTOR, not set as property on instance
    }) {
        //console.log('Hello from the top of NEW QuoteSheetItemVariant constructor');
        this.id = id; 
        this.guid = guid;
        //this.quotesheet_item_id = quotesheet_item_id || quotesheet_item.id;  // NEW - get from quotesheet_item_id first, or quotesheet_item.id
        this.description = description;
        this.sort_order = toNumberOrNull(sort_order);
        this.quantity = toNumberOrNull(quantity);
        this.uom = uom;
        this.quantity_timeframe = quantity_timeframe;
        this.need_by_date = need_by_date;  // TODO - use Luxon?
        this.target_price = toNumberOrNull(target_price);
        this.target_price_currency = target_price_currency || default_currency;
        this.current_price = toNumberOrNull(current_price);
        this.current_price_currency = current_price_currency || default_currency;
        // JUST FOR DEBUGGING
        if (this.target_price !== null && !this.target_price_currency) {
            console.error(`In QuoteSheetItemVariant constructor, we have target_price ${this.target_price} but NO target_price_currency!`);
        }
        if (this.current_price !== null && !this.current_price_currency) {
            console.error(`In QuoteSheetItemVariant constructor, we have current_price ${this.current_price} but NO current_price_currency!`);
        }
        this.qs_item_guid = qs_item_guid || quotesheet_item.guid;
        // vendor col fields -- Boolean versions
        this.enable_price = enable_price;
        this.enable_subtotal = enable_subtotal;
        // just use price/subtotal for these?  yes
        //this.enable_quoted_price = enable_quoted_price;
        //this.enable_quoted_subtotal = enable_quoted_subtotal;
        this.enable_comment = enable_comment;
        this.enable_moq = enable_moq;
        this.enable_leadtime = enable_leadtime;
        this.enable_deliverydate = enable_deliverydate;
        this.enable_country_of_origin = enable_country_of_origin;
        this.enable_shipterms = enable_shipterms;
        this.enable_payterms = enable_payterms;
        this.enable_nobid = enable_nobid;
    }
    // STATIC (class methods/properties)
    static disabledValues() {
        // return an OBJECT with the correct values for each field when that field is disabled (ie: column turned off in grid)
        // only include fields/cols that can be turned off
        return {
            description: '',
            quantity: null,
            uom: '',
            quantity_timeframe: '',
            need_by_date: null,
            target_price: null,
            // target_price_currency: '', // we don't need to 'blank' this out if price is disabled...  will be ignored on backend if price is null.  works either way (blanking or not blanking) -- but we won't blank it, because otherwise we have to re-set it if the col is turned back on if page is not reloaded
            current_price: null,
            // current_price_currency: '', // we don't need to 'blank' this out if price is disabled...  will be ignored on backend if price is null.  works either way (blanking or not blanking) -- but we won't blank it, because otherwise we have to re-set it if the col is turned back on if page is not reloaded
            enable_price: false,
            enable_subtotal: false,
            //enable_quoted_price: false,  // we are just using enable_price / enable_subtotal
            //enable_quoted_subtotal: false,  // we are just using enable_price / enable_subtotal
            enable_comment: false,
            enable_moq: false,
            enable_leadtime: false,
            enable_deliverydate: false,
            enable_country_of_origin: false,
            enable_shipterms: false,
            enable_payterms: false,
            enable_nobid: false,
        }
    }
    // GETTERS
    // METHODS 
    setCurrency(curCode) {
        // method to call from grid to change the currency we're using in the grid
        // when we do this, if currency code is CHANGING we must reset the AMOUNT too
        // don't reset if existing currency is not set? ('' instead of 'USD' or something... although that hopefully no longer happens)
        if (curCode !== this.target_price_currency && this.target_price_currency) {  // if CHANGING, and we have a truthy existing currency
            this.target_price_currency = curCode;
            this.target_price = null;  // null is our blank value for prices, not zero
        }
        if (curCode !== this.current_price_currency && this.current_price_currency) {  // if CHANGING, and we have a truthy existing currency
            this.current_price_currency = curCode;
            this.current_price = null;
        }
    }
    formatForApi(overrideObj) {  // return version of itself in format ready to POST to API
        // can pass in optional overrideObj -- object that will override any key/values in here before returning it
        // return a new object so no need to clone in caller 
        // how to make sure we pass the right 'empty/blank' values?  use the correct valueParser in each column so blank text values are kept as empty strings ''
        let apiObj = {...this}; // make shallow copy - this is what we'll return - NOTE: this won't copy the methods, I think, in case you're using this other places
        //apiObj.sort_order = sortOrder;  // set 1-based sort_order
        if (overrideObj) {
            apiObj = Object.assign(apiObj, overrideObj);
        }
        return apiObj;
    }
    clone(initialArgs) {
        // clone this object, resetting certain things as needed (does NOT reset sort_order... do that with initialArgs if needed)
        // initialArgs is an optional OBJECT of defaults to use, gets merged in before making new instance (but cannot set 'id' or 'guid' -- those get reset no matter what)
        // a bit of a hack, but we can just run through formatForApi(dummy_sort_order), reset some things, then pass that to class constructor
        // doing that to make sure we have all params in correct format/type
        let cloneArgs = this.formatForApi();
        if (initialArgs) {
            cloneArgs = Object.assign(cloneArgs, initialArgs);
        }
        cloneArgs.id = '';  // blank this since its not saved to backend yet
        cloneArgs.guid = uuidv4();  // assign new guid
        return new QuoteSheetItemVariant(cloneArgs);
    }
    getInvalidFields() {
        // return an ARRAY of field / property names that HAVE ERRORS / are NOT valid 
        // used so you can highlight those cells specifically
        let requiredFields = ['guid', 'qs_item_guid'];
        let invalidFields = requiredFields.filter(fld => !hasValueOrZero(this[fld]));  // will return empty array if all OK
        let vendorColsAtLeastOne = vendorColsAll.some(vcol => this[vcol.enableKey]);  // uses canonical vendorColsAll from this file - these are booleans, so no need for hasValueOrZero()
        if (!vendorColsAtLeastOne) {
            invalidFields.push('vendor_cols');  // use 'vendor_cols' to mean a general error about vendor_cols
        }
        return invalidFields;
    }
    isValid() {
        // validate if this Variant is valid on its own, but does not validate 'parent' QuoteSheetItem
        // for DRY, just call getInvalidFields and see if we have any 'errors'
        return this.getInvalidFields().length === 0;
    }
    isEmpty() {
        // is this VARIANT completely empty?  if true, AND IF qs_item is also completely empty, we'll discard this before saving via API
        // what is empty?  no text entered in non-vendor-input cells, no linked product
        const emptyCheckFields = [
            'description', 'quantity', 'uom', 'quantity_timeframe', 
            'need_by_date', 'target_price', 'current_price',
        ];
        // make sure they're all falsey?  or different than default values for them?  
        // checking if different than defaults seems more strict, but maybe too strict
        // just check if all falsey for now
        return emptyCheckFields.every(fld => !this[fld]);
    }
}

const getDefaultQsItemData = function() {  // args/object to use when creating new QuoteSheetItem
    return {
        guid: uuidv4(),
        sort_order: 1,  // likely override this when making blank one
        // any other default values?
    };
}

const makeBlankQsItem = function(initialData) {  // initialData should be an object with any starting values
    let defaultData = getDefaultQsItemData();  // initialData will override anything in here
    let data = Object.assign({}, defaultData, initialData);
    //console.log('makeBlankQsItem with this data:');
    //console.log(data);
    return new QuoteSheetItem(data);
}

const getDefaultVariantData = function() { // args/object to use when creating new QuoteSheetItemVariant
    return {
        guid: uuidv4(),
        sort_order: 1,  // likely override this when making new one
        // any other default values?
    };
}

const makeBlankVariant = function(initialData) {
    let defaultData = getDefaultVariantData();
    let data = Object.assign({}, defaultData, initialData);
    //console.log('makeBlankVariant with this data:');
    //console.log(data);
    return new QuoteSheetItemVariant(data);
}



// AG-GRID COMPONENTS

var ActionButtonComponent = Vue.extend({
    // can access your main component holding the grid using this.params.context.componentParent
    data: function() {
        return {
            showConfirmDelete: false,  // local data only - toggle to show/hide confirmDelete
            isOpen: false,  // using .sync with open prop on v-popover
            showLoading: false, // local data only, set to true to show spinner while we're saving item to DB, etc
            isEditAllowed: false,  // local value, set from value of parent -- must make sure to refresh if changed in parent
            // 'computed' type properties that we'll update via refresh()
            hasOneVariant: false,
            hasMultipleVariants: false,
            isGroupColumn: false,  // also determines if we delete GROUP or VARIANT
            isVariantColumn: false,
            qsItem: null, // get the QuoteSheetItem for this line from gridContext -- need it to see if we have linkedProduct anyways
            hasLinkedProduct: false,  // set to true if the qsItem has a linkedProduct
            canSaveProduct: false,  // set to true if we don't already have linkedProduct, but we could call save method
        }
    },
    mounted: function() {
        /* console.log('params from actionButtonComponent');
        console.log(this.params); */
        this.updateData(this.params);
    },
    computed: {
        // computed properties don't get refreshed on cellRefresh
        // instead, must trigger things to update in refresh() method... so call a method from there
    },
    methods: {
        refresh: function(params) {
            // called when the cell should be refreshed -- use this for anything that depends on group/parent data
            this.updateData(params);
            return false;
        },
        updateData: function(params) {
            //console.log('updateData');
            //console.log(params.value);
            // check if editing is allowed or not on parent
            this.isEditAllowed = params.context.componentParent.isEditAllowed;
            // update things
            this.hasOneVariant = params.node.firstChild && params.node.lastChild;
            this.hasMultipleVariants = params.node.parent.allChildrenCount > 1;
            this.isGroupColumn = params.column.colId === 'actions';
            this.isVariantColumn = params.column.colId === 'description';
            this.qsItem = params.context.componentParent.getQsItem(this.params.data.qs_item_guid);
            this.hasLinkedProduct = this.qsItem ? this.qsItem.hasLinkedProduct : false;
            this.canSaveProduct = this.qsItem ? this.qsItem.canCreateLinkedProduct() : false;  // use method on qsItem for consistency
        },
        performDelete: function() {
            // perform the delete -- either group delete or node delete, depending on isGroupColumn
            this.params.context.componentParent.deleteNode({
                rowNode: this.params.node,
                group: this.isGroupColumn,
            });
            this.closeAndReset();
        },
        cancelDelete: function() {  // just close and reset
            this.closeAndReset();
        },
        cloneNode: function() {
            // for cloning just this variant line
            this.params.context.componentParent.cloneNode(this.params.node, false, false);
            this.closeAndReset();
        },
        cloneGroup: function() {
            // for cloning the entire group this is part of
            this.params.context.componentParent.cloneNode(this.params.node, false, true);
            this.closeAndReset();
        },
        addVariant: function(position) {
            // position can be 'above', 'below' or 'end' -- default is 'below' if not specified
            // add a variant to the same group as this rowNode 
            this.params.context.componentParent.addVariant(this.params.node, position);
            this.closeAndReset();
        },
        saveItem: function() {
            // save this row to the sourcing database
            this.showLoading = true;
            console.log('From component, save item to DB');
            this.params.context.componentParent.saveItemToSourcingDb(this.params.node)
                .then(() => {  // if we resolve to true or false in api call, we'll go here, which is OK
                    this.closeAndReset();
                })
        },
        clearLinkedProduct() {
            // go through parent component -- to be consistent with other ones, and so it redraws the row
            this.params.context.componentParent.clearLinkedProduct(this.params.node);
            this.closeAndReset();
        },
        emitAndClose: function(eventName) {
            // generic helper function to emit various events, then close popover
            // pass in event name such as 'clone' or 'add-variant'
            // this will add rowNode object (params.node) to emit event, then close the popover
            this.$emit(eventName, this.params.node);
            this.closeAndReset();
        },
        closeAndReset: function() {
            // DRY function to close popover & reset all things (in that order, so it doesn't flash other things before closing)
            // need to reset things because component doesn't get destroyed for some time after closing
            this.isOpen = false;
            this.showConfirmDelete = false;
            this.showLoading = false;
        }
    },
    template: `
    <div class="d-flex align-items-center">
        <v-popover
            offset="8"
            :open.sync="isOpen"
            auto-hide
            :handleResize="false"
            :disabled="!isEditAllowed"
            placement="right"
            popover-inner-class="popover-inner"
        >  
            <!-- This will be the popover target (for the events and position) -->
            <b-link>
                <b-iconstack font-scale="1.5" class="tooltip-target">
                    <b-icon stacked icon="three-dots" scale="0.75" class=""></b-icon>
                    <b-icon stacked icon="circle" class=""></b-icon>
                </b-iconstack>
            </b-link>
        
            <!-- This will be the content of the popover -->
            <template slot="popover" class="p-0">
                <div v-if="showConfirmDelete" key="confirmDelete" class="py-2 px-3">
                    <div class="font-weight-bold mb-1 text-center">Delete Row</div>
                    <div class="mb-2 text-center">
                        <div>Are you sure?</div>
                        <div v-if="isGroupColumn && hasMultipleVariants" class="font-weight-bold mt-1">This will also delete all item variants.</div>
                    </div>
                    <b-button size="sm" block variant="sharp-secondary" @click="cancelDelete">No, Cancel</b-button>
                    <b-button size="sm" block variant="danger" @click="performDelete">Yes, Delete</b-button>
                </div>
                <div v-else-if="showLoading" key="loading" class="py-3 px-4">
                    <b-spinner small></b-spinner>
                </div>
                <div v-else class="d-flex flex-column">
                    <a v-if="isGroupColumn && hasOneVariant" @click="cloneGroup" href="#" class="dropdown-item">Duplicate Line</a>
                    <a v-if="isGroupColumn && hasMultipleVariants" @click="cloneGroup" href="#" class="dropdown-item">Duplicate Group</a>
                    <a v-if="isGroupColumn" @click="addVariant('end')" href="#" class="dropdown-item">Add Variant</a>
                    <a v-if="isVariantColumn" @click="cloneNode" href="#" class="dropdown-item">Duplicate Line</a>
                    <a v-if="canSaveProduct && isGroupColumn" @click="saveItem" href="#" class="dropdown-item">Save to Sourcing Database</a>
                    <a v-if="hasLinkedProduct && isGroupColumn" @click="clearLinkedProduct" href="#" class="dropdown-item">Unlink from Saved Item</a>
                    <a @click="showConfirmDelete = true" href="#" class="dropdown-item">{{ (isGroupColumn && hasMultipleVariants) ? 'Delete Group' : 'Delete Line' }}</a>
                </div>
            </template>
        </v-popover>
        <div class="ml-2">{{ params.valueFormatted || params.value }}</div>
    </div>
    `
});

var QuoteSheetItemFieldRenderer = function(params) {  // NON-VUE VERSION (faster) - use this in colDefs --> cellRendererParams: {placeholder: 'Some Placeholder'}
    // use this in item_name, item_part_number, item_description on QuoteSheetBuilder, shows things from linked_product in special way
    // takes extra param:  
    //  - 'placeholder' as an extra param -- params.placeholder
    // THIS COUNTS ZERO 0 as a valid value, will not show placeholder -- if you want to show placeholder, value should be empty string
    // wrap this in a span with class like 'simple-placeholder' and/or 'placeholder-colId' to make css easier?
    if (!params.column) return '';
    let hasValue = hasValueOrZero(params.value);
    let value = hasValue ? (params.valueFormatted || params.value) : params.placeholder
    let cellDiv = document.createElement('div');
    // only add grid-placeholder-text class if its showing the placeholder
    let hasLinkedProduct = params.context.componentParent.getQsItem(params.data.qs_item_guid).hasLinkedProduct;
    let linked_product_span = hasLinkedProduct ? '<b-icon icon="box" class="ml-1"></b-icon>' : '';
    //let linked_product_span = 'hello';
    cellDiv.innerHTML = `<span class="${hasValue ? '' : 'grid-placeholder-text '}simple-placeholder placeholder-col-${params.column.colId}">` + value + linked_product_span + `</span>`;
    return cellDiv;
}

var QuoteSheetItemVueRenderer = Vue.extend({
    // can access your main component holding the grid using this.params.context.componentParent
    /* other params to pass in:
        - qsItemField - string (or null) for the field name to get value from on qsItem (null = don't get it)
        - linkedProductField - string (or null) for the field name to get value from on linkedProduct (null = don't get it)
        - placeholder - string, to use if no value
    */
    data: function() {
        return {
            qsItem: null,  // the QuoteSheetItem object from context, default to null, so we can easily check for falsey value
            linkedProduct: null,  // the LinkedProduct object from context, IF ANY, default to null so we can easily check for falsey value
            localValue: '',  // the final value to show, either from qsItem, or linkedProduct, or placeholder
            valueFromLinkedProduct: false,  // boolean, set to true if localValue is from linkedProduct so we show icon
            isPlaceholder: true,  // if value is just placeholder, set to true
            showLock: false,  // if true, we'll show a lock to mean this can't be changed...
            lockColIds: ['item_name', 'item_part_number'],  // if we are in one of these columns and have valueFromLinkedProduct, we'll showLock
        }
    },
    computed: {
        linkedProductUrl() {
            return this.linkedProduct?.url;
        },
    },
    mounted: function() {
        this.updateData(this.params);
    },
    methods: {
        refresh: function(params) {
            // called when the cell should be refreshed -- use this for anything that depends on group/parent data
            this.updateData(params);
            return false;  // return true to reuse component?  false to make new one?
        },
        updateData: function(params) {
            // update all local data -- call this on mounted? and on refresh()
            this.qsItem = params.context.componentParent.getQsItem(this.params.data.qs_item_guid);
            this.linkedProduct = (this.qsItem && this.qsItem.hasLinkedProduct) ? params.context.componentParent.getLinkedProduct(this.qsItem.linked_product_id) : null;
            // now set the localValue
            let isLockedCol = this.lockColIds.includes(params.column.colId);
            let qsItemValue = (params.qsItemField && this.qsItem) ? this.qsItem[params.qsItemField] : '';
            let linkedProductValue = (params.linkedProductField && this.linkedProduct) ? this.linkedProduct[params.linkedProductField] : '';
            // NOTE - make sure this matches how things are shown on reply side (geez... what a mess)
            // Feb 17 2023 - it seems this and reply side do not match -- reply side uses linked product values if it has them, always
            //          so... we're just switching around order of things - check if you have linkedProduct first.. if so, use those values, then else if has qsItemValue, then placeholders
            if (this.linkedProduct) {  // start with this check first
                let linkedProductPlaceholder = isLockedCol ? '---' : params.placeholder;  // if its a locked col, don't show normal placeholder, show some dummy
                this.localValue = linkedProductValue || linkedProductPlaceholder;
                this.valueFromLinkedProduct = true;
                this.isPlaceholder = false;
                this.showLock = isLockedCol;
            } else if (qsItemValue) {  // if we have a value from qsItem, use that
                this.localValue = qsItemValue;
                this.valueFromLinkedProduct = false;
                this.isPlaceholder = false;
                this.showLock = false;
            } else {  // if no qsItem value, and no linkedProduct, just show placeholder
                this.localValue = params.placeholder;  
                this.valueFromLinkedProduct = false;  
                this.isPlaceholder = true;
                this.showLock = false;
            }
        },
    },
    template: `
    <div 
        :class="[
            'placeholder-col-' + params.column.colId, 
            isPlaceholder ? 'grid-placeholder-text' : '',
            valueFromLinkedProduct ? 'linked-product-value' : '',
        ]"
    >
        {{ localValue }}
        <a :href="linkedProductUrl || '#'" target="_blank">
            <b-icon 
                v-if="showLock" 
                icon="box" 
                class="ml-1" 
                font-scale="0.8" 
                variant="primary" 
                style="vertical-align: baseline;"
            ></b-icon>
        </a>
    </div>
    `
})


export {
    QuoteSheetItem,
    QuoteSheetItemVariant,
    ActionButtonComponent,
    QuoteSheetItemFieldRenderer,
    QuoteSheetItemVueRenderer,
    makeBlankQsItem,
    makeBlankVariant,
}